import { Component, Input, OnInit } from '@angular/core';
import { EventsService } from '../../../../../../../../@shared/services/events-service';
import { ToastService } from '../../../../../../../../@core/services/toast.service';
import { EventResponse, EventContactResponse, EventTaskResponse, EventMemberResponse } from '../../../../../../../../@shared/models/response.module';

@Component({
  selector: 'app-manage-event-tasks',
  standalone: false,
  templateUrl: './manage-event-tasks.html',
  styleUrl: './manage-event-tasks.css'
})
export class ManageEventTasks implements OnInit {
  @Input() event: EventResponse;

  loading: boolean = true;
  tasks: EventTaskResponse[] = [];
  contacts: EventContactResponse[] = [];
  members: EventMemberResponse[] = [];

  // Filter
  filterAssignee: string | null = null;

  // Quick add
  quickAddTitle: string = '';
  quickAddLoading: boolean = false;

  // Add/Edit form
  showForm: boolean = false;
  editingTask: EventTaskResponse | null = null;
  formTitle: string = '';
  formDescription: string = '';
  formAssignee: string | null = null; // "member:USR-xxx" or "contact:5"
  formDueDate: string = '';
  formLoading: boolean = false;

  // Toggle tracking
  togglingTaskId: number | null = null;

  // Completed section
  showCompleted: boolean = false;

  constructor(
    private eventsService: EventsService,
    private toastService: ToastService
  ) {}

  ngOnInit() {
    this.loadContacts();
    this.loadMembers();
    this.loadTasks();
  }

  loadContacts() {
    this.eventsService.getEventContacts({
      event_id: this.event.event_id,
    }).subscribe({
      next: (contacts) => {
        this.contacts = contacts || [];
      }
    });
  }

  loadMembers() {
    this.eventsService.getEventMembers({ event_id: this.event.event_id }).subscribe({
      next: (members) => {
        this.members = members || [];
      }
    });
  }

  loadTasks() {
    this.loading = this.tasks.length === 0;
    this.eventsService.getEventTasks({ event_id: this.event.event_id }).subscribe({
      next: (tasks) => {
        this.tasks = tasks || [];
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  get remainingTasks(): EventTaskResponse[] {
    return this.filteredTasks.filter(t => !t.is_completed);
  }

  get completedTasks(): EventTaskResponse[] {
    return this.filteredTasks.filter(t => t.is_completed);
  }

  get filteredTasks(): EventTaskResponse[] {
    if (!this.filterAssignee) return this.tasks;
    if (this.filterAssignee.startsWith('member:')) {
      const userId = this.filterAssignee.substring(7);
      return this.tasks.filter(t => t.assigned_to_user_id === userId);
    }
    if (this.filterAssignee.startsWith('contact:')) {
      const contactId = Number(this.filterAssignee.substring(8));
      return this.tasks.filter(t => t.assigned_to_contact_id === contactId);
    }
    return this.tasks;
  }

  // Quick add
  quickAdd() {
    if (!this.quickAddTitle.trim()) return;
    this.quickAddLoading = true;
    this.eventsService.createEventTask({
      event_id: this.event.event_id,
      title: this.quickAddTitle.trim(),
    }).subscribe({
      next: () => {
        this.quickAddTitle = '';
        this.quickAddLoading = false;
        this.loadTasks();
      },
      error: () => {
        this.toastService.error('Failed to create task');
        this.quickAddLoading = false;
      }
    });
  }

  // Form methods
  openAddForm() {
    this.editingTask = null;
    this.formTitle = '';
    this.formDescription = '';
    this.formAssignee = null;
    this.formDueDate = '';
    this.showForm = true;
  }

  openEditForm(task: EventTaskResponse) {
    this.editingTask = task;
    this.formTitle = task.title;
    this.formDescription = task.description || '';
    if (task.assigned_to_user_id) {
      this.formAssignee = 'member:' + task.assigned_to_user_id;
    } else if (task.assigned_to_contact_id) {
      this.formAssignee = 'contact:' + task.assigned_to_contact_id;
    } else {
      this.formAssignee = null;
    }
    this.formDueDate = task.due_date ? task.due_date.substring(0, 10) : '';
    this.showForm = true;
  }

  cancelForm() {
    this.showForm = false;
    this.editingTask = null;
  }

  private parseAssignee(): { assigned_to_contact_id?: number | null; assigned_to_user_id?: string | null } {
    if (!this.formAssignee) {
      return { assigned_to_contact_id: null, assigned_to_user_id: null };
    }
    if (this.formAssignee.startsWith('member:')) {
      return { assigned_to_contact_id: null, assigned_to_user_id: this.formAssignee.substring(7) };
    }
    if (this.formAssignee.startsWith('contact:')) {
      return { assigned_to_contact_id: Number(this.formAssignee.substring(8)), assigned_to_user_id: null };
    }
    return { assigned_to_contact_id: null, assigned_to_user_id: null };
  }

  saveTask() {
    if (!this.formTitle.trim()) return;
    this.formLoading = true;
    const assignee = this.parseAssignee();

    if (this.editingTask) {
      this.eventsService.updateEventTask({
        task_id: this.editingTask.task_id,
        event_id: this.event.event_id,
        title: this.formTitle.trim(),
        description: this.formDescription.trim() || undefined,
        assigned_to_contact_id: assignee.assigned_to_contact_id,
        assigned_to_user_id: assignee.assigned_to_user_id,
        due_date: this.formDueDate || null
      }).subscribe({
        next: () => {
          this.formLoading = false;
          this.showForm = false;
          this.editingTask = null;
          this.loadTasks();
        },
        error: () => {
          this.toastService.error('Failed to update task');
          this.formLoading = false;
        }
      });
    } else {
      this.eventsService.createEventTask({
        event_id: this.event.event_id,
        title: this.formTitle.trim(),
        description: this.formDescription.trim() || undefined,
        assigned_to_contact_id: assignee.assigned_to_contact_id || undefined,
        assigned_to_user_id: assignee.assigned_to_user_id || undefined,
        due_date: this.formDueDate || undefined
      }).subscribe({
        next: () => {
          this.formLoading = false;
          this.showForm = false;
          this.loadTasks();
        },
        error: () => {
          this.toastService.error('Failed to create task');
          this.formLoading = false;
        }
      });
    }
  }

  toggleTask(task: EventTaskResponse) {
    this.togglingTaskId = task.task_id;
    this.eventsService.toggleEventTask({ task_id: task.task_id }).subscribe({
      next: () => {
        this.togglingTaskId = null;
        this.loadTasks();
      },
      error: () => {
        this.togglingTaskId = null;
        this.toastService.error('Failed to update task');
      }
    });
  }

  deleteTask(task: EventTaskResponse) {
    this.eventsService.deleteEventTask({ task_id: task.task_id }).subscribe({
      next: () => {
        this.loadTasks();
      },
      error: () => {
        this.toastService.error('Failed to delete task');
      }
    });
  }

  getDueDateBadgeClass(task: EventTaskResponse): string {
    if (!task.due_date || task.is_completed) return 'task-badge-upcoming';
    const due = new Date(task.due_date);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const dueDay = new Date(due.getFullYear(), due.getMonth(), due.getDate());

    if (dueDay < today) return 'task-badge-overdue';
    if (dueDay.getTime() === today.getTime()) return 'task-badge-today';
    return 'task-badge-upcoming';
  }

  getTaskAssigneeName(task: EventTaskResponse): string | null {
    if (task.assigned_to_user) {
      return (task.assigned_to_user.first_name + ' ' + task.assigned_to_user.last_name).trim();
    }
    if (task.assigned_to) {
      return (task.assigned_to.first_name + ' ' + task.assigned_to.last_name).trim();
    }
    return null;
  }

  getTaskAssigneeShortName(task: EventTaskResponse): string | null {
    if (task.assigned_to_user) {
      return task.assigned_to_user.first_name;
    }
    if (task.assigned_to) {
      return task.assigned_to.first_name;
    }
    return null;
  }

  getDueDateLabel(task: EventTaskResponse): string {
    if (!task.due_date) return '';
    const due = new Date(task.due_date);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const dueDay = new Date(due.getFullYear(), due.getMonth(), due.getDate());
    const diffDays = Math.round((dueDay.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return `${Math.abs(diffDays)}d overdue`;
    if (diffDays === 0) return 'Due today';
    if (diffDays === 1) return 'Tomorrow';
    return due.toLocaleDateString('nb-NO', { day: '2-digit', month: 'short' });
  }
}
